import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/components/ui/cn'
import { WordPopup } from '@/components/reading/WordPopup'
import { extractSentenceContext } from '@/components/reading/extractSentenceContext'
import { readMultiWordSelection } from '@/components/reading/readMultiWordSelection'
import {
  findCoveringPhraseIndices,
  findFirstPhraseIndices,
} from '@/components/reading/findCoveringPhraseIndices'
import { stripStoryMarkdown, tokenizeContent } from '@/lib/tokenizeContent'

interface StoryDisplayProps {
  content: string
  /** Words to highlight even if not bolded by AI (optional safety net). */
  targetWords?: string[]
  /** Lowercased lemmas already in the user's vocabulary — used for in-vocab styling. */
  userVocabWords?: Set<string>
  className?: string
  onWordAdded?: (word: string) => void
}

interface PopupState {
  selection: string
  rect: DOMRect
  context: string
  /** Flat token index across rendered story tokens; null for multi-word selection. */
  tokenIndex: number | null
}

/**
 * Renders an AI-generated story with clickable words and vocabulary highlighting.
 */
export function StoryDisplay({
  content,
  targetWords = [],
  userVocabWords,
  className,
  onWordAdded,
}: StoryDisplayProps) {
  const [popup, setPopup] = useState<PopupState | null>(null)
  const [activeTokenIndices, setActiveTokenIndices] = useState<Set<number>>(() => new Set())
  const [flashTokenIndices, setFlashTokenIndices] = useState<Set<number>>(() => new Set())
  const flashAnimTimerRef = useRef<number | null>(null)
  const storyRef = useRef<HTMLParagraphElement>(null)
  const targetSet = useMemo(() => new Set(targetWords.map((w) => w.toLowerCase())), [targetWords])
  const plainContent = useMemo(() => stripStoryMarkdown(content), [content])
  const segments = useMemo(() => parseMarkdownBold(content, targetSet), [content, targetSet])

  const flatTokens = useMemo(() => {
    const out: { text: string; isWord: boolean; lower: string; bold: boolean }[] = []
    for (const segment of segments) {
      for (const token of tokenizeContent(segment.text)) {
        out.push({
          text: token.text,
          isWord: token.isWord,
          lower: token.lower,
          bold: segment.bold,
        })
      }
    }
    return out
  }, [segments])

  const closePopup = useCallback(() => {
    setPopup(null)
    setActiveTokenIndices(new Set())
    setFlashTokenIndices(new Set())
  }, [])

  const runEntranceFlash = useCallback((indices: number[]) => {
    if (flashAnimTimerRef.current != null) {
      window.clearTimeout(flashAnimTimerRef.current)
    }
    const next = new Set(indices)
    setActiveTokenIndices(next)
    setFlashTokenIndices(new Set())
    window.requestAnimationFrame(() => {
      setFlashTokenIndices(next)
      flashAnimTimerRef.current = window.setTimeout(() => {
        setFlashTokenIndices(new Set())
        flashAnimTimerRef.current = null
      }, 1400)
    })
  }, [])

  const handleWordClick = useCallback(
    (word: string, tokenIndex: number, event: React.MouseEvent<HTMLSpanElement>) => {
      const rect = event.currentTarget.getBoundingClientRect()
      setPopup({
        selection: word,
        rect,
        context: extractSentenceContext(plainContent, word),
        tokenIndex,
      })
      runEntranceFlash([tokenIndex])
    },
    [plainContent, runEntranceFlash],
  )

  const handleStoryMouseUp = useCallback(() => {
    const container = storyRef.current
    if (!container) return

    const picked = readMultiWordSelection(container)
    if (!picked) return

    window.getSelection()?.removeAllRanges()
    setPopup({
      selection: picked.text,
      rect: picked.rect,
      context: extractSentenceContext(plainContent, picked.text),
      tokenIndex: null,
    })
    setActiveTokenIndices(new Set())
    setFlashTokenIndices(new Set())
  }, [plainContent])

  const handleSpanFlash = useCallback((phraseSurface: string) => {
    const asContentTokens = flatTokens.map((t) => ({
      text: t.text,
      isWord: t.isWord,
      lower: t.lower,
    }))
    const indices =
      popup?.tokenIndex != null
        ? findCoveringPhraseIndices(asContentTokens, popup.tokenIndex, phraseSurface)
        : findFirstPhraseIndices(asContentTokens, phraseSurface)
    if (!indices?.length) {
      return
    }
    runEntranceFlash(indices)
  }, [flatTokens, popup?.tokenIndex, runEntranceFlash])

  useEffect(() => {
    return () => {
      if (flashAnimTimerRef.current != null) {
        window.clearTimeout(flashAnimTimerRef.current)
      }
    }
  }, [])
  return (
    <>
      <div className={cn('prose prose-slate dark:prose-invert max-w-none', className)}>
        <p
          ref={storyRef}
          className="whitespace-pre-wrap leading-8 text-base"
          onMouseUp={handleStoryMouseUp}
        >
          {flatTokens.map((token, tokenIndex) => {
            if (!token.isWord) {
              return <span key={tokenIndex}>{token.text}</span>
            }

            const inVocab = userVocabWords?.has(token.lower) ?? false
            const highlighted = token.bold || targetSet.has(token.lower)

            return (
              <span
                key={tokenIndex}
                onClick={(e) => handleWordClick(token.lower, tokenIndex, e)}
                data-word-clickable="true"
                data-span-flash={activeTokenIndices.has(tokenIndex) ? 'true' : undefined}
                className={cn(
                  'cursor-pointer transition-colors',
                  highlighted
                    ? inVocab
                      ? 'rounded bg-primary/15 px-0.5 font-semibold text-foreground underline decoration-primary/60 decoration-2 hover:bg-primary/25 dark:bg-primary/35 dark:decoration-primary dark:hover:bg-primary/50'
                      : 'rounded bg-primary/15 px-0.5 font-semibold text-foreground hover:bg-primary/25 dark:bg-primary/35 dark:hover:bg-primary/50'
                    : inVocab
                      ? 'rounded bg-primary/10 px-0.5 underline decoration-primary/40 decoration-2 hover:bg-primary/20 dark:bg-primary/30 dark:decoration-primary/80 dark:hover:bg-primary/45'
                      : 'underline decoration-dotted decoration-muted-foreground/50 hover:decoration-primary/70 dark:decoration-muted-foreground/80',
                  activeTokenIndices.has(tokenIndex) && 'lexio-span-active',
                  flashTokenIndices.has(tokenIndex) && 'lexio-span-flash',
                )}
              >
                {token.text}
              </span>
            )
          })}
        </p>
      </div>

      {popup ? (
        <WordPopup
          mode="practice"
          selection={popup.selection}
          context={popup.context}
          anchorRect={popup.rect}
          onClose={closePopup}
          onWordAdded={onWordAdded}
          onSpanFlash={handleSpanFlash}
        />
      ) : null}
    </>
  )
}

interface Segment {
  text: string
  bold: boolean
}

function parseMarkdownBold(text: string, targetSet: Set<string>): Segment[] {
  const segments: Segment[] = []
  const regex = /\*\*(.+?)\*\*/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const plain = text.slice(lastIndex, match.index)
      segments.push(...splitByTargetWords(plain, targetSet, false))
    }
    segments.push({ text: match[1], bold: true })
    lastIndex = regex.lastIndex
  }

  if (lastIndex < text.length) {
    const rest = text.slice(lastIndex)
    segments.push(...splitByTargetWords(rest, targetSet, false))
  }

  return segments
}

function splitByTargetWords(text: string, targetSet: Set<string>, defaultBold: boolean): Segment[] {
  if (targetSet.size === 0) return [{ text, bold: defaultBold }]

  const escapedWords = [...targetSet].map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const pattern = new RegExp(`\\b(${escapedWords.join('|')})\\b`, 'gi')

  const segments: Segment[] = []
  let last = 0
  let m: RegExpExecArray | null

  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) {
      segments.push({ text: text.slice(last, m.index), bold: defaultBold })
    }
    segments.push({ text: m[1], bold: true })
    last = pattern.lastIndex
  }

  if (last < text.length) {
    segments.push({ text: text.slice(last), bold: defaultBold })
  }

  return segments
}
